import type { CSSProperties } from "react";
import { useEffect, useRef, useState } from "react";
import { cn } from "../lib/utils";

interface TextRevealProps {
  text: string;
  by?: "word" | "char";
  delay?: number;
  duration?: number;
  trigger?: "mount" | "visible";
  className?: string;
  style?: CSSProperties;
}

export function TextReveal({
  text,
  by = "word",
  delay = 0,
  duration = 500,
  trigger = "mount",
  className,
  style,
}: TextRevealProps) {
  const [revealed, setRevealed] = useState(false);
  const ref = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    if (trigger === "mount") {
      const raf = requestAnimationFrame(() => {
        const t = setTimeout(() => setRevealed(true), delay);
        return () => clearTimeout(t);
      });
      return () => cancelAnimationFrame(raf);
    }
    if (trigger === "visible") {
      const el = ref.current;
      if (!el) return;
      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            setTimeout(() => setRevealed(true), delay);
            observer.disconnect();
          }
        },
        { threshold: 0.2 }
      );
      observer.observe(el);
      return () => observer.disconnect();
    }
  }, [trigger, delay]);

  const tokens = by === "word" ? text.split(" ") : text.split("");

  return (
    <span ref={ref} className={cn("text-reveal", className)} style={style} aria-label={text}>
      {tokens.map((token, i) => (
        <span
          key={i}
          className="text-reveal-token"
          style={{
            "--reveal-delay": `${(i * duration * 0.5) / (tokens.length || 1)}ms`,
            "--reveal-duration": `${duration}ms`,
            opacity: revealed ? 1 : 0,
            transform: revealed ? "none" : "translateY(1em)",
          } as CSSProperties}
        >
          {token}
          {by === "word" && i < tokens.length - 1 ? " " : ""}
        </span>
      ))}
    </span>
  );
}
